/**
 * Bulk Actions Modal Component - Detachable Admin Module
 * 
 * This component provides bulk action modals for credit and plan management across multiple users.
 */

import React, { useState, useEffect } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { formatCredits, getPlanInfo, getUserDisplayName } from '../utils';
import type { CreditType, PlanType, PlatformUser } from '../types';

interface BulkActionsModalProps {
  users: PlatformUser[];
  action: 'addCredits' | 'changePlan' | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface OperationResult {
  userId: string;
  userName: string;
  success: boolean;
  error?: string;
}

/**
 * Modal component for bulk credit and plan management actions
 */
export const BulkActionsModal: React.FC<BulkActionsModalProps> = ({
  users,
  action,
  onClose,
  onSuccess
}) => {
  const { operations } = useAdminData();
  
  // Form state
  const [creditForm, setCreditForm] = useState({
    amount: '',
    creditType: 'free_platform' as CreditType,
    description: ''
  });
  
  const [planForm, setPlanForm] = useState({
    planType: 'starter' as PlanType,
    monthlyLimit: ''
  });
  
  // Processing state
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<OperationResult[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);

  // Reset state when modal opens or action changes
  useEffect(() => {
    if (action) {
      // Clear previous results and reset state
      setResults([]);
      setCurrentProgress(0);
      operations.clearError();
      
      // Reset forms to default values
      setCreditForm({
        amount: '',
        creditType: 'free_platform' as CreditType,
        description: ''
      });
      
      setPlanForm({
        planType: 'starter' as PlanType,
        monthlyLimit: ''
      });
    }
  }, [action]); // Reset whenever action changes or modal opens

  const handleBulkAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amount = parseFloat(creditForm.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }

    setProcessing(true);
    const operationResults: OperationResult[] = [];

    // Process each user sequentially
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      setCurrentProgress(Math.round(((i + 1) / users.length) * 100));
      
      try {
        const success = await operations.addCredits(
          user.id || user.email,
          amount,
          creditForm.creditType,
          creditForm.description || `Credit Grant`
        );
        
        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success
        });
      } catch (error: any) {
        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success: false,
          error: error.message || 'Operation failed'
        });
      }
    }

    setResults(operationResults);
    setProcessing(false);
    
    // If all successful, close after a short delay
    const allSuccess = operationResults.every(r => r.success);
    if (allSuccess) {
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    }
  };

  const handleBulkChangePlan = async (e: React.FormEvent) => {
    e.preventDefault();

    setProcessing(true);
    const operationResults: OperationResult[] = [];

    // Process each user sequentially
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      setCurrentProgress(Math.round(((i + 1) / users.length) * 100));
      
      try {
        const monthlyLimit = planForm.monthlyLimit ? parseFloat(planForm.monthlyLimit) : undefined;
        
        const success = await operations.changePlan(
          user.id || user.email,
          planForm.planType,
          undefined,  // weeklyLimit removed
          monthlyLimit
        );
        
        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success
        });
      } catch (error: any) {
        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success: false,
          error: error.message || 'Operation failed'
        });
      }
    }

    setResults(operationResults);
    setProcessing(false);
    
    // If all successful, close after a short delay
    const allSuccess = operationResults.every(r => r.success);
    if (allSuccess) {
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    }
  };

  const handleClose = () => {
    if (!processing) {
      operations.clearError();
      onClose();
    }
  };

  if (!action || users.length === 0) return null;

  // Show results after processing
  if (results.length > 0) {
    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Bulk Operation Results
            </h3>
            
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-green-600 font-medium">✓ {successCount} Successful</span>
                {failureCount > 0 && (
                  <span className="text-red-600 font-medium">✗ {failureCount} Failed</span>
                )}
              </div>
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {results.map((result, index) => (
                <div
                  key={result.userId}
                  className={`p-3 rounded-md flex items-center justify-between ${
                    result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`text-lg ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                      {result.success ? '✓' : '✗'}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{result.userName}</div>
                      {result.error && (
                        <div className="text-xs text-red-600">{result.error}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4 mt-4 border-t">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {failureCount > 0 ? 'Close' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Add Credits Modal */}
          {action === 'addCredits' && (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Bulk Add Credits ({users.length} users)
              </h3>
              
              <form onSubmit={handleBulkAddCredits} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-md mb-4">
                  <p className="text-sm text-blue-800 mb-2 font-medium">
                    Selected Users:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {users.map(user => (
                      <p key={user.id || user.email} className="text-xs text-blue-700">
                        • {getUserDisplayName(user)} ({user.email})
                      </p>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="10000"
                    value={creditForm.amount}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    required
                    autoFocus
                    disabled={processing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This amount will be added to each selected user
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Type
                  </label>
                  <select
                    value={creditForm.creditType}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, creditType: e.target.value as CreditType }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    disabled={processing}
                  >
                    <option value="free_platform">Free Platform Credits</option>
                    <option value="monthly">Monthly Credits</option>
                    <option value="paid_platform">Paid Platform Credits</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={creditForm.description}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Reason for bulk credit grant..."
                    disabled={processing}
                  />
                </div>

                {processing && (
                  <div className="bg-blue-50 p-3 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-800">Processing...</span>
                      <span className="text-sm font-medium text-blue-900">{currentProgress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${currentProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {operations.error && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {operations.error}
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={processing}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Add Credits to All'}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Change Plan Modal */}
          {action === 'changePlan' && (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Bulk Change Plan ({users.length} users)
              </h3>
              
              <form onSubmit={handleBulkChangePlan} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-md mb-4">
                  <p className="text-sm text-blue-800 mb-2 font-medium">
                    Selected Users:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {users.map(user => (
                      <p key={user.id || user.email} className="text-xs text-blue-700">
                        • {getUserDisplayName(user)} ({user.email})
                      </p>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Plan Type
                  </label>
                  <select
                    value={planForm.planType}
                    onChange={(e) => setPlanForm(prev => ({ ...prev, planType: e.target.value as PlanType }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    disabled={processing}
                    required
                  >
                    <option value="starter">Starter Plan</option>
                    <option value="monthly">Monthly Plan</option>
                    <option value="unlimited">Unlimited Plan</option>
                  </select>
                </div>

                {planForm.planType === 'monthly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Credit Limit ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planForm.monthlyLimit}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Leave empty to use default"
                      disabled={processing}
                    />
                  </div>
                )}

                {processing && (
                  <div className="bg-blue-50 p-3 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-800">Processing...</span>
                      <span className="text-sm font-medium text-blue-900">{currentProgress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${currentProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {operations.error && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {operations.error}
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={processing}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Change Plan for All'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkActionsModal;

